import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core'
import { ITerritory, User } from '../../models/User'
import { UserService } from '../../services/user.service'
import { AuthenticationService, ContextService } from '../../services'
import { IFilter } from '../../common/table/table.component'
import { UserFormComponent } from '../user-form/user-form.component'
import { TranslateService } from '@ngx-translate/core'

declare let navigator: any

@Component({
  standalone: false,
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
})
export class UserComponent implements OnInit {
  users: User[] = []
  loading = false

  columns = [
    {
      id: 'username',
      label: 'User',
    },
    {
      id: 'email',
      label: 'Email',
    },
    {
      id: 'territoriesToString',
      label: 'Territories',
    },
  ]

  filter: IFilter = {
    icon: '/assets/img/icons/positive-clear/search-darker.svg',
    placeholder: 'Search users and territories',
    filterFunc: (item, inputValue) => {
      return (
        this.cleanString(item.territoriesToString).includes(
          this.cleanString(inputValue),
        ) ||
        this.cleanString(item.username).includes(
          this.cleanString(inputValue.toLowerCase()),
        )
      )
    },
  }
  deleteLoading = false

  territoriesPlaceholder: ITerritory[]
  createdUser: User

  get selectedUsers() {
    return this.users.filter((user) => user.selected)
  }

  @ViewChild('modal', { static: true }) modal: any
  @ViewChild('modalSuccess', { static: true }) modalSuccess: any

  constructor(
    private context: ContextService,
    private userService: UserService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private auth: AuthenticationService,
  ) {}

  ngOnInit() {
    this.loading = true
    this.userService.getTerritories().then((territories: ITerritory[]) => {
      this.territoriesPlaceholder = territories
    })
    this.userService.getUsers().then((users) => {
      users.forEach((user) => {
        this.users.push(this._processData(new User(user)))
      })
      this.loading = false
    })
  }

  _processData(item) {
    const LIMIT_TERR = 2
    const territories = item.territories
      .slice(0, LIMIT_TERR)
      .map((location) => location.name + ` (${location.region_id})`)
    let territoriesText = territories.join('   ·   ')
    if (item.territories.length > LIMIT_TERR) {
      territoriesText += `   ·   +${Math.abs(
        LIMIT_TERR - item.territories.length,
      )} more`
    }
    return {
      ...item,
      territoriesToString: territoriesText,
    }
  }

  isUserSelected() {
    return this.selectedUsers.length !== 0
  }

  isMeSelected() {
    return this.selectedUsers.find((user) => this.isMe(user)) !== undefined
  }

  removeUserRowInTable(deletedUser: User) {
    const userIdx = this.users.findIndex(
      (user) => deletedUser.username === user.username,
    )
    if (userIdx !== -1) {
      this.users.splice(userIdx, 1)
    }
  }

  deleteUsers() {
    if (this.isUserSelected() && !this.isMeSelected()) {
      this.deleteLoading = true
      const selectedUsers = [...this.selectedUsers]
      const userDeletePromises = this.selectedUsers.map((user) =>
        this.userService.deleteUser(user),
      )
      Promise.all(userDeletePromises).then((_) => {
        selectedUsers.forEach((selectedUser: User) =>
          this.removeUserRowInTable(selectedUser),
        )
        if (!this.cdr['destroyed']) {
          this.cdr.detectChanges()
        }
        this.deleteLoading = false
      })
    }
  }

  rowClicked(event) {
    this.openModal(event)
  }

  checkboxClicked(event) {
    event.selected = !event.selected
  }

  openModal(selectedItem = null) {
    const component = this.context.createComponentInstance(
      UserFormComponent,
      false,
    )
    this.modal.error = null
    this.modal.success = null
    this.modal.title = selectedItem ? 'Account' : 'New account'
    this.modal.acceptButtonText = selectedItem ? 'Save changes' : 'Save'
    component.instance.territoriesPlaceholder = [
      ...this.territoriesPlaceholder,
    ] as ITerritory[]
    component.instance.modal = this.modal
    component.instance.onDelete.subscribe((deletedUser: User) =>
      this.removeUserRowInTable(deletedUser),
    )
    this.modal.onAccept.subscribe(({ entity }) => {
      this.modal.error = null
      component.instance.checkErrors()
      if (component.instance.errors.length) {
        return
      }

      if (entity.isAdmin) {
        entity.territories = []
      }

      navigator.clipboard.writeText(entity.password)

      this.modal.acceptLoading = true

      let res
      if (component.instance.isEdit) {
        res = this.userService.editUser(
          component.instance.entityCopy.username,
          entity,
        )
      } else {
        res = this.userService.createUser(entity)
      }
      res
        .then((_) => {
          this.modal.acceptLoading = false
          this.modal.hide()
          if (component.instance.isEdit) {
            const userIdx = this.users.findIndex(
              (user) => entity.username === user.username,
            )
            this.users[userIdx] = this._processData(entity)
          } else {
            this.createdUser = entity
            this.modalSuccess.show()
            this.users.push(this._processData(entity))
          }
          this.users.sort((a, b) => ('' + a.username).localeCompare(b.username))
        })
        .catch((err) => {
          this.modal.acceptLoading = false
          this.modal.error = {
            message: this.translate.instant(
              err.error.error && ('' + err.error.error.status)[0] === '4'
                ? err.error.message === 'Error creating user'
                  ? err.error.message +
                    '. ' +
                    this.translate.instant('Try again in a few minutes.')
                  : err.error.message
                : this.translate.instant("There's been an error.") +
                    ' ' +
                    this.translate.instant(`Try again in a few minutes.`),
            ),
          }
        })
    })
    if (selectedItem) {
      this.modal.loading = true
      this.userService.getUser(selectedItem.username).then((user) => {
        this.modal.loading = false
        this.modal.cdr.detectChanges()
        component.instance.entity = new User({
          ...user,
          territories: selectedItem.territories,
        })
        this.modal.component = component
        this.modal.show()
      })
    } else {
      this.modal.acceptDisabled = false
      this.modal.component = component
      this.modal.acceptButtonText = this.translate.instant('Create')
    }

    this.modal.show()
  }

  copyPassword() {
    navigator.clipboard.writeText(this.createdUser.password)
  }

  closeModalSuccess() {
    this.modalSuccess.hide()
  }

  private cleanString(str) {
    const specialChars = '!@#$^&%*()+=-[]/{}|:<>?,.'

    for (let i = 0; i < specialChars.length; i++) {
      str = str.replace(new RegExp('\\' + specialChars[i], 'gi'), '')
    }

    str = str.toLowerCase()

    str = str.replace(/ /g, '_')

    str = str.replace(/á/gi, 'a')
    str = str.replace(/é/gi, 'e')
    str = str.replace(/í/gi, 'i')
    str = str.replace(/ó/gi, 'o')
    str = str.replace(/ú/gi, 'u')
    str = str.replace(/ñ/gi, 'n')
    return str
  }

  isMe(user: User) {
    return this.auth.getUser().user.username === user.username
  }
}
